import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';
import {
  SystemIcon,
  SystemLabel,
} from '#apps/client-web/features/audit-form-mechanical/constants';

import { type AuditMechanicalFormEntry } from '#packages/data-collection/models/audit-mechanical-form';
import { type System } from '#packages/data-collection/models/common/system';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { type AuditFormMechanicalPageViewModel } from './audit-form-mechanical-page.view-model';
import { AuditFormMechanicalPageRenderer } from './audit-form-mechanical-page.renderer';

export type AuditFormMechanicalPageContainerProps =
  ViewModelProps<AuditFormMechanicalPageViewModel> & LoggingContext;

export const AuditFormMechanicalPageContainer: FC<
  AuditFormMechanicalPageContainerProps
> = ({ viewModel }) => {
  const { auditId, auditFormQuery, onCreate } = viewModel;

  const systems = useMemo(
    () =>
      Object.keys(SystemLabel)
        .map((system) => ({
          id: system,
          label: SystemLabel[system as System],
          Icon: SystemIcon[system as System],
          entriesLinkMap: Object.fromEntries(
            (auditFormQuery.data?.entries ?? [])
              .map<
                [string, AuditMechanicalFormEntry]
              >((item, index) => [routes.audit.getEnergyAuditMechanicalFormEntryPath(auditId!, index), item])
              .filter(([, item]) => item.system === system),
          ),
        }))
        .filter((system) => Object.keys(system.entriesLinkMap).length > 0),
    [auditFormQuery.data],
  );

  const isLoading = auditFormQuery.isLoading;
  const isEmpty = auditFormQuery.isSuccess && systems.length < 1;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {/* Loading */}
      {isLoading && <LoadingPage />}

      {/* Empty */}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}

      {/* Data */}
      {hasData && <AuditFormMechanicalPageRenderer systems={systems} />}

      {/* FAB */}
      {hasData && <Fab onClick={onCreate} />}

      {/* Error */}
      {auditFormQuery.isError && <ThrowError error={auditFormQuery.error} />}
    </Fragment>
  );
};
